import { motion } from "framer-motion";
import { User, ExternalLink, BarChart3, Globe, Linkedin, BookOpen, Zap } from "lucide-react";
import { Layout, ExternalLinkCard } from "@/components/Layout";
import { useLanguage } from "@/lib/LanguageContext";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";
import { EXTERNAL_LINKS } from "@/lib/index";

export default function GiovanniPage() {
  const { t, language } = useLanguage();

  const EXPERTISE = [
    "B2B SaaS Growth Marketing",
    "Technical SEO & Demand Generation",
    "GTM Strategy & Systems Architecture",
    "AI Integration in Business Processes",
    "LinkedIn Organic Growth & 360Brew",
    "Content Systems & Brand Architecture",
    "Cross-Functional Program Management",
    "ROI-driven Marketing Architecture",
  ];

  const LINKS = [
    {
      href: EXTERNAL_LINKS.GIOVANNI_LINKEDIN,
      title: "LinkedIn Profile",
      description: language === "it" 
        ? "Connettiti con Giovanni Burdi su LinkedIn per aggiornamenti su AI, growth marketing e strategie digitali."
        : language === "de"
        ? "Vernetzen Sie sich mit Giovanni Burdi auf LinkedIn für Updates zu KI, Growth Marketing und digitalen Strategien."
        : "Connect with Giovanni Burdi on LinkedIn for updates on AI, growth marketing, and digital strategies.",
      icon: Linkedin,
      badge: "LinkedIn",
    },
    {
      href: EXTERNAL_LINKS.ROI_ARCHITECT,
      title: "The ROI Architect™",
      description: language === "it"
        ? "La company LinkedIn di Giovanni: framework, insights e risorse per costruire sistemi di crescita scalabili."
        : language === "de"
        ? "Giovannis LinkedIn-Unternehmen: Frameworks, Einblicke und Ressourcen für den Aufbau skalierbarer Wachstumssysteme."
        : "Giovanni's LinkedIn company: frameworks, insights, and resources for building scalable growth systems.",
      icon: BarChart3,
      badge: "Company Page",
    },
    {
      href: EXTERNAL_LINKS.VIEWPOINT,
      title: "The Viewpoint Newsletter",
      description: language === "it"
        ? "La newsletter LinkedIn di Giovanni con analisi settimanali su AI, marketing e strategie di crescita B2B."
        : language === "de"
        ? "Giovannis LinkedIn-Newsletter mit wöchentlichen Analysen zu KI, Marketing und B2B-Wachstumsstrategien."
        : "Giovanni's LinkedIn newsletter with weekly analyses on AI, marketing, and B2B growth strategies.",
      icon: BookOpen,
      badge: "Newsletter",
    },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8"
          style={{ boxShadow: "0 8px 40px oklch(0.10 0.03 235 / 0.6)" }}
        >
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-primary/30 flex-shrink-0">
              <img src="/logo.png" alt="Giovanni Burdi" className="w-full h-full object-cover" />
            </div>
            <div className="text-center sm:text-left space-y-2 flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/15 text-accent text-xs font-semibold mb-1">
                <Zap className="w-3 h-3" />
                Creator of LinkedIn Profile Optimizer
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground">{t("giovanni.title")}</h1>
              <p className="text-sm sm:text-base text-primary font-semibold">
                {t("giovanni.subtitle")}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
                {t("giovanni.description")}
              </p>
              <div className="pt-2">
                <a
                  href={EXTERNAL_LINKS.GIOVANNI_LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                  style={{ boxShadow: "0 4px 16px oklch(0.70 0.20 175 / 0.35)" }}
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Expertise */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.1 }}
          className="space-y-4"
        >
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {t("giovanni.expertise")}
          </h2>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid sm:grid-cols-2 gap-3"
          >
            {EXPERTISE.map((skill, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-all duration-200"
              >
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground font-medium">{skill}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* Links */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.2 }}
          className="space-y-4"
        >
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            {t("giovanni.resources")}
          </h2>
          <div className="space-y-3">
            {LINKS.map((link, i) => (
              <ExternalLinkCard key={i} {...link} />
            ))}
          </div>
        </motion.section>

        {/* 360Brew Tool CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center space-y-3"
        >
          <h3 className="font-bold text-foreground text-lg">{t("giovanni.cta")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("giovanni.ctaDesc")}
          </p>
          <a
            href="#/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <Zap className="w-4 h-4" />
            {t("home.analyzeBtn")}
          </a>
        </motion.div>
      </div>
    </Layout>
  );
}
